import { TZDate } from '@date-fns/tz';
import type {
  Locale
} from 'date-fns';
import {
  parse,
  format,
  toDate,
  isValid,
  setHours,
  parseJSON,
  setMinutes,
  setSeconds,
  setDefaultOptions,
  startOfDay,
  addDays
} from 'date-fns';

import { DEFAULT_DATE_LOCALE, UTC } from 'constants/Date';
import { NONE } from 'constants/Global';
import { DEFAULT_LANGUAGE } from 'constants/LanguageConstants';

import parseISODuration from 'polyfill/parseISODuration';

let getModulePromise: Promise<Locale> | null = null;

export const getCurrentDateLocale = async () => (getModulePromise ?? DEFAULT_DATE_LOCALE);

export const setDateLocale = (language: string): void => {
  if (language === DEFAULT_LANGUAGE) {
    setDefaultOptions({ locale: DEFAULT_DATE_LOCALE });
    getModulePromise = Promise.resolve(DEFAULT_DATE_LOCALE);
  } else {
    getModulePromise = import(`date-fns-locale/locale/${language}.js`)
      .then(module => {
        const locale = module.default as Locale;
        setDefaultOptions({ locale });
        return locale;
      })
      .catch((error: unknown) => {
        console.error(`Failed to load date-fns locale for ${language}`, error);
        setDefaultOptions({ locale: DEFAULT_DATE_LOCALE });
        return DEFAULT_DATE_LOCALE;
      });
  }
};

/** Returns the current date with given time (hours, minutes, seconds) */
export const getTodayAtTime = (hours: number, minutes: number = 0, seconds: number = 0) => setSeconds(
  setMinutes(setHours(new Date(), hours), minutes),
  seconds
);

/**
 * Converts a date from a timezone to another.
 * ⚠️ WARNING ⚠️
 * Returned date will still have the same timezone as the original date, but adjusted in the destination timezone.
 * Example:
 * let date = new Date(2024, 3, 22, 12); // Mon Apr 22 2024 12:00:00 GMT+0200 (Central European Summer Time)
 * convertTimeZones(date, 'Europe/Paris', 'UTC'); // Mon Apr 22 2024 10:00:00 GMT+0200 (Central European Summer Time)
 * 12:00 CEST is 10:00 UTC, but the date still shows CEST timezone.
 * */
export const convertTimeZones = (date: any, timezoneOrigin: string, timezoneDestination: string) => {
  if (timezoneOrigin === timezoneDestination) {
    return toDate(date);
  }

  if (timezoneOrigin === UTC) {
    return new TZDate(date, timezoneDestination);
  }

  return new TZDate(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    date.getMilliseconds(),
    timezoneOrigin
  ).withTimeZone(timezoneDestination);
};

/**
 * Converts a date from a given timezone to a UTC string.
 * @param {Date} date - The date to convert.
 * @param {string} timezoneOrigin - The timezone of the date.
 * @returns {string} The stringified date in UTC timezone (ISO string).
 */
export const timezoneToUTCString = (date: any, timezoneOrigin: string) => new Date(
  new TZDate(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    date.getMilliseconds(),
    timezoneOrigin
  ).getTime()
).toISOString();

/**
 * @typedef {Object} TryParseOptions
 * @property {string?} formatOrigin - The format of the date to parse.
 * @property {string?} formatDestination - The format to return the date in.
 */

/**
 * Tries to parse a date from a string, a number, or a date.
 * @param {string|number|Date|null|undefined} date - The date to parse.
 * @param {TryParseOptions?} options - The options to use.
 * @returns {Date|string|undefined} The parsed date (stringified using provided format, if any), or undefined if it could not be parsed.
 */

export const tryParse = (date: any, { formatOrigin, formatDestination }: any = {}) => {
  if (date) {
    let parsed;

    if (isValid(date)) {
      parsed = toDate(date);
    }

    if (!isValid(parsed) && formatOrigin) {
      parsed = parse(date, formatOrigin, new Date());
    }

    if (!isValid(parsed)) {
      parsed = parseJSON(date.toString());
    }

    if (isValid(parsed)) {
      return formatDestination ? format(parsed, formatDestination) : parsed;
    }
  }

  return undefined;
};

/**
 * Converts a date to an ISO string from a dealer timezone.
 * ⚠️ This is intended to be used with selectors.
 * @example `getParameters` in `src/modules/audits/selectors/dashboard.js`
 * @param {string|number|Date} [date] - The date to convert.
 * @param {string} [dealerTimeZone] - The dealer timezone.
 * @returns {string} The ISO string representation of the date.
 */
export const getDateFilter = (date: any, dealerTimeZone: string) => {
  let parsedDate = tryParse(date);

  if (parsedDate) {
    parsedDate = convertTimeZones(parsedDate, Intl.DateTimeFormat().resolvedOptions().timeZone, dealerTimeZone).toISOString();
  }

  return parsedDate;
};

// 0 is there to be disabled, 60 is there to allow hours incrementation
export const QUARTERS = [0, 15, 30, 45, 60];

/**
 * @param {import('hooks/useDealerTimeZone').ToDealerTimeZone} toDealerTimeZone
 * @returns {Date}
 */
export const getStartOfTomorrow = (toDealerTimeZone: any) => startOfDay(addDays(toDealerTimeZone(new Date()), 1));

/**
 * Returns the start of the day from the given `date` (given in `originTimeZone`) and converts it to UTC taking account of dealer timezone.
 *
 * @example ```js
 *   getTimezonedStartOfDayAsUTCString(new Date(2025, 8, 15, 9, 15), 'Pacific/Chatam', UTC) // returns '2025-09-15T11:15:00.000Z'
 * ```
 */
export const getTimezonedStartOfDayAsUTCString = (date: any, dealerTimeZone: string, originTimeZone: string = UTC) => timezoneToUTCString(
  startOfDay(
    convertTimeZones(date, originTimeZone, dealerTimeZone)
  ),
  dealerTimeZone
);

/**
 * Returns the number of hours encoded in an ISO 8601 duration string (e.g. "PT24H" -> 24).
 * Used to compare durations expressed as period keys without relying on their position in an array.
 */
export const getPeriodHours = (periodKey: string): number => (parseISODuration(periodKey) as { hours: number }).hours;

/**
 * Returns whether periodKey encodes a strictly shorter duration than referencePeriodKey.
 * A missing or `NONE` value on either side is treated as "no constraint" and returns true.
 */
export const isPeriodStrictlyShorterThan = (periodKey?: string, referencePeriodKey?: string): boolean => {
  if (!periodKey || periodKey === NONE || !referencePeriodKey || referencePeriodKey === NONE) {
    return true;
  }
  return getPeriodHours(periodKey) < getPeriodHours(referencePeriodKey);
};
